import { isNonEmployedJobType, useFeatureFlags } from '@applyonline/shared'
import { EFinancingProgram } from '@if/core/types'
import { formatCurrency, formatPhone } from '@if/core/utils'
import { Applicant } from '../../../types/Schemas/ApplicantSchema'
import { first } from 'lodash-es'
import { useTranslation } from 'react-i18next'

interface Props {
  applicant: Applicant
  hasApplicantError: boolean
  hasApplicantAddressError: boolean
  hasApplicantRevenueError: boolean
  gotoStep: (target: string) => void
}
const ReviewApplicantInfo = ({
  applicant,
  hasApplicantError,
  hasApplicantAddressError,
  hasApplicantRevenueError,
  gotoStep,
}: Props) => {
  const { t } = useTranslation()
  const [flags] = useFeatureFlags(EFinancingProgram.Personal)
  const useExactMonths = flags?.useExactMonthsUnderOneYear ?? false
  const yearsAtCurrentAddress = Math.floor(applicant.currentAddress.months / 12)
  return (
    <>
      <div className="review-item">
        <h1 className="h3">{t(applicant.isCoapplicant ? 'common.aboutCoapplicant' : 'common.aboutYourself')}</h1>
        <div className="box">
          <div className="box-content">
            <p>
              <strong>{t('personalInformation.preferredLanguage')} : </strong>
              <span>{t(`ELanguage.${applicant.languageId}`, { ns: 'enum' })}</span>
            </p>
            <p>
              <strong>{t('personalInformation.yourGender')} : </strong>
              <span>{t(`EGender.${applicant.genderId}`, { ns: 'enum' })}</span>
            </p>
            <p>
              <strong>{t('personalInformation.yourFirstName')} : </strong>
              <span>{applicant.firstName}</span>
            </p>
            <p>
              <strong>{t('personalInformation.yourLastName')} : </strong>
              <span>{applicant.lastName}</span>
            </p>
            <p>
              <strong>{t('confirmation.birthDate')} : </strong>
              <span>{applicant.birthDate}</span>
            </p>
            <p>
              <strong>{t('confirmation.mobilePhone')} : </strong>
              <span>{formatPhone(applicant.cellPhone)}</span>
            </p>
            {applicant.homePhone && (
              <p>
                <strong>{t('confirmation.homePhone')} : </strong>
                <span>{formatPhone(applicant.homePhone)}</span>
              </p>
            )}
            <p>
              <strong>{t('confirmation.email')} : </strong>
              <span>{applicant.email}</span>
            </p>
            {applicant.sin && (
              <p>
                <strong>{t('personalInformation.sin')} : </strong>
                <span>{applicant.sin}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            className={hasApplicantError ? 'btn-error btn-simple-error' : 'btn-action btn-simple'}
            onClick={() => gotoStep('personal-information')}
          >
            {t('review.modify')}
          </button>
        </div>
      </div>

      <div className="review-item">
        <h1 className="h3">{t('common.yourHome')}</h1>
        <div className="box">
          <div className="box-content">
            <p>
              <strong>{t('confirmation.address')} : </strong>
              <span>
                {applicant.currentAddress.apartment && `${applicant.currentAddress.apartment}-`}
                {applicant.currentAddress.civicNumber} {applicant.currentAddress.street},{' '}
                {applicant.currentAddress.city}, {applicant.currentAddress.stateIso}{' '}
                {applicant.currentAddress.postalCode}
              </span>
            </p>
            <p>
              <strong>{t('common.since')} : </strong>
              {yearsAtCurrentAddress === 0 ? (
                <span>
                  {useExactMonths
                    ? t('common.monthsLabel', { count: applicant.currentAddress.months })
                    : t('common.lessThanOneYear')}
                </span>
              ) : (
                <span>{t('common.yearsLabel', { count: yearsAtCurrentAddress })}&nbsp;</span>
              )}
            </p>
            <p>
              <strong>{t('housing.homeFeeType')} : </strong>
              <span>{t(`EHomeStatus.${applicant.spending.homeFeeTypeId}`, { ns: 'enum' })}</span>
            </p>
          </div>
          <button
            type="button"
            className={hasApplicantAddressError ? 'btn-error btn-simple-error' : 'btn-action btn-simple'}
            onClick={() => gotoStep('address')}
          >
            {t('review.modify')}
          </button>
        </div>
      </div>

      <div className="review-item">
        <h1 className="h3">{t('income.howMuch')}</h1>
        <div className="box">
          <div className="box-content">
            {applicant.currentJobs?.[0] && !isNonEmployedJobType(applicant.currentJobs[0].jobType) && (
              <>
                <p>
                  <strong>{t('income.category')} : </strong>
                  <span> {t(`EJobType.${applicant.currentJobs[0].jobType}`, { ns: 'enum' })}</span>
                </p>
                <p>
                  <strong>{t('income.jobTitle')} : </strong>
                  <span> {applicant.currentJobs[0].jobTitle}</span>
                </p>
                <p>
                  <strong>{t('income.beforeDeductions')} : </strong>
                  <span> {formatCurrency(applicant.currentJobs[0].annualSalary)}</span>
                </p>
                <p>
                  <strong>{t('income.employerName')} : </strong>
                  <span> {applicant.currentJobs[0].employerName}</span>
                </p>
                <p>
                  <strong>{t('income.phoneNumber')} : </strong>
                  <span> {formatPhone(applicant.currentJobs[0].employerPhone)}</span>
                </p>
                <p>
                  <strong>{t('income.extension')} : </strong>
                  <span> {applicant.currentJobs[0].employerPhoneExt}</span>
                </p>

                <p>
                  <strong>{t('income.employmentLenght')} : </strong>
                  {applicant.currentJobs[0].years === -1 ? (
                    <span>
                      {useExactMonths
                        ? t('common.monthsLabel', { count: applicant.currentJobs[0].months ?? 0 })
                        : t('common.lessThanOneYear')}
                    </span>
                  ) : (
                    applicant.currentJobs[0].years && (
                      <span>
                        {t('common.yearsLabel', {
                          count: applicant.currentJobs[0].years,
                        })}
                        &nbsp;
                      </span>
                    )
                  )}
                </p>
              </>
            )}
            <p>
              <strong>{t('income.otherIncome')} : </strong>
              <span> {formatCurrency(first(applicant.otherIncomes)?.annualAmount)}</span>
            </p>
          </div>
          <button
            type="button"
            className={hasApplicantRevenueError ? 'btn-error btn-simple-error' : 'btn-action btn-simple'}
            onClick={() => gotoStep('employment')}
          >
            {t('review.modify')}
          </button>
        </div>
      </div>
    </>
  )
}

export default ReviewApplicantInfo
